import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { analyticsApi } from '../services/api';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Loading from '../components/Loading';
import './Analytics.css';

function Analytics() {
  const navigate = useNavigate();
  const { user, authLoading } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [eventType, setEventType] = useState('');

  const loadStats = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (eventType) params.event_type = eventType;
      
      const response = await analyticsApi.getStats(params);
      if (response.data.success) {
        setStats(response.data.data);
      } else {
        setError(response.data.message || 'Failed to load analytics');
      }
    } catch (err) {
      console.error('Error loading analytics:', err);
      setError(err.response?.data?.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, eventType]);

  useEffect(() => {
    if (!authLoading && (!user || user.profile !== 'admin')) {
      navigate('/admin');
      return;
    }
    if (user) {
      // Set default date range (last 30 days)
      const today = new Date();
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(today.getDate() - 30);
      
      setDateFrom(thirtyDaysAgo.toISOString().split('T')[0]);
      setDateTo(today.toISOString().split('T')[0]);
      
      // loadStats will be called after state updates via separate effect
    }
  }, [user, authLoading, navigate]);

  // Load stats when dateFrom, dateTo, or eventType changes
  useEffect(() => {
    if (dateFrom && dateTo) {
      loadStats();
    }
  }, [dateFrom, dateTo, eventType, loadStats]);

  const handleFilterChange = () => {
    loadStats();
  };

  if (authLoading || !user) {
    return (
      <>
        <Header />
        <div className="container">
          <Loading />
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="container">
        <div className="analytics-page">
          <div className="admin-header">
            <h1>Analytics Dashboard</h1>
            <Link to="/admin" className="btn btn-secondary back-btn">
              <i className="bi bi-arrow-left"></i> Back to GR Admin
            </Link>
          </div>
          
          <p className="page-description">
            Visitor analytics and traffic statistics. View page views, events, and top IP addresses with geographic origin.
          </p>

          {/* Filters */}
          <div className="analytics-filters">
            <div className="filter-group">
              <label htmlFor="date-from">From:</label>
              <input
                type="date"
                id="date-from"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                onBlur={handleFilterChange}
              />
            </div>
            <div className="filter-group">
              <label htmlFor="date-to">To:</label>
              <input
                type="date"
                id="date-to"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                onBlur={handleFilterChange}
              />
            </div>
            <div className="filter-group">
              <label htmlFor="event-type">Event Type:</label>
              <select
                id="event-type"
                value={eventType}
                onChange={(e) => {
                  setEventType(e.target.value);
                  handleFilterChange();
                }}
              >
                <option value="">All Events</option>
                <option value="page_view">Page Views</option>
                <option value="search">Search</option>
                <option value="interaction">Interaction</option>
              </select>
            </div>
            <button className="btn btn-primary" onClick={handleFilterChange}>
              Apply Filters
            </button>
          </div>

          {error && <div className="alert alert-danger">{error}</div>}

          {loading ? (
            <Loading />
          ) : stats ? (
            <>
              {/* Summary Cards */}
              <div className="analytics-summary">
                <div className="summary-card">
                  <div className="summary-label">Total Events</div>
                  <div className="summary-value">{stats.total_events || 0}</div>
                </div>
                <div className="summary-card">
                  <div className="summary-label">Unique Visitors</div>
                  <div className="summary-value">{stats.unique_visitors || 0}</div>
                </div>
                <div className="summary-card">
                  <div className="summary-label">Date Range</div>
                  <div className="summary-value">
                    {stats.date_range?.from} to {stats.date_range?.to}
                  </div>
                </div>
              </div>

              {/* Top IP Addresses */}
              <div className="stats-section">
                <h2>Top IP Addresses</h2>
                <div className="stats-table-container">
                  <table className="table table-striped table-hover stats-table">
                    <thead>
                      <tr>
                        <th>IP Address</th>
                        <th>Country</th>
                        <th>Region</th>
                        <th>City</th>
                        <th>ISP</th>
                        <th>Events</th>
                      </tr>
                    </thead>
                    <tbody>
                      {!stats.top_ip_addresses || !Array.isArray(stats.top_ip_addresses) || stats.top_ip_addresses.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="text-center">
                            No IP address data available for the selected date range.
                            {stats.total_events > 0 && (
                              <div style={{ marginTop: '0.5rem', fontSize: '0.9rem', opacity: 0.8 }}>
                                (Note: IP addresses may not be tracked if analytics events don't include IP data)
                              </div>
                            )}
                          </td>
                        </tr>
                      ) : (
                        stats.top_ip_addresses.map((ip, index) => (
                          <tr key={index}>
                            <td className="ip-address-cell">{ip.ip_address || 'N/A'}</td>
                            <td>{ip.country || 'Unknown'}</td>
                            <td>{ip.region || '-'}</td>
                            <td>{ip.city || '-'}</td>
                            <td>{ip.isp || '-'}</td>
                            <td className="event-count">{ip.count || 0}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Events by Day */}
              {stats.events_by_day && stats.events_by_day.length > 0 && (
                <div className="stats-section">
                  <h2>Events by Day</h2>
                  <div className="stats-table-container">
                    <table className="table table-striped table-hover stats-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Count</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stats.events_by_day.map((day, index) => (
                          <tr key={index}>
                            <td>{day.date || '-'}</td>
                            <td className="event-count">{day.count || 0}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Events by Type */}
              {stats.events_by_type && stats.events_by_type.length > 0 && (
                <div className="stats-section">
                  <h2>Events by Type</h2>
                  <div className="stats-table-container">
                    <table className="table table-striped table-hover stats-table">
                      <thead>
                        <tr>
                          <th>Event Type</th>
                          <th>Count</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stats.events_by_type.map((event, index) => (
                          <tr key={index}>
                            <td>{event.event_type}</td>
                            <td className="event-count">{event.count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Top Pages */}
              {stats.page_views && stats.page_views.length > 0 && (
                <div className="stats-section">
                  <h2>Top Pages</h2>
                  <div className="stats-table-container">
                    <table className="table table-striped table-hover stats-table">
                      <thead>
                        <tr>
                          <th>Page</th>
                          <th>Views</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stats.page_views.map((page, index) => (
                          <tr key={index}>
                            <td>{page.page || '-'}</td>
                            <td className="event-count">{page.views}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Top Referrers */}
              {stats.top_referrers && stats.top_referrers.length > 0 && (
                <div className="stats-section">
                  <h2>Top Referrers</h2>
                  <div className="stats-table-container">
                    <table className="table table-striped table-hover stats-table">
                      <thead>
                        <tr>
                          <th>Referrer</th>
                          <th>Count</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stats.top_referrers.map((referrer, index) => (
                          <tr key={index}>
                            <td className="referrer-cell">{referrer.referrer}</td>
                            <td className="event-count">{referrer.count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="alert alert-info">No analytics data available</div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
}

export default Analytics;
